import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { getRenderedFieldByType } from "../../entries/utils/getRenderedFieldByType";
import { Flex } from "../../../components/Flex";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { moduleActions } from "../../../redux/module/module.slice";
import isEmpty from "lodash/isEmpty";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { Drawer } from "../../../components/Drawer";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { useLocation } from "react-router-dom";
import { entryActions } from "../../../redux/entry/entry.slice";
import { PageMeta } from "../../../model/shared/meta/PageMeta";
import { DrawerDefaultsExtra } from "../../tasks/components/DrawerDefaultsExtra";
import { Input } from "../../../components/Input";
import { ModuleInstanceAttachment } from "../../../model/module/types/module-instance-attachment/ModuleInstanceAttachment";
import { UpdateModuleInstanceAttachmentDto } from "../../../model/module/dto/module-instance-attachment/UpdateModuleInstanceAttachmentDto";
import { BaseModuleInstance } from "../../../model/module/types/module-instance/BaseModuleInstance";
import isFunction from "lodash/isFunction";
import { useDrawersContext } from "../../tasks/drawers-context";

type ModuleInstanceAttachmentUpdateModalProps = {
  open: boolean;
  onClose: VoidFunction;
  attachment?: ModuleInstanceAttachment | null;
  setSelectedInstance?: ATVoidFunction<BaseModuleInstance>;
};

export const ModuleInstanceAttachmentUpdateModal: React.FC<
  ModuleInstanceAttachmentUpdateModalProps
> = ({ open = false, onClose, attachment, setSelectedInstance }) => {
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const { setEntry } = useDrawersContext();
  const { control, setError, reset, getValues, handleSubmit } =
    useForm<UpdateModuleInstanceAttachmentDto>();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const instancesPage = useAppSelector(
    moduleSliceSelectors.selectInstancesPage,
  );
  const instancesPageSize = useAppSelector(
    moduleSliceSelectors.selectInstancesPageSize,
  );
  const parentModuleDefinition = useAppSelector((s) =>
    moduleSliceSelectors.selectModuleDefinitionById(
      s,
      attachment?.parentModuleDefinitionId || "",
    ),
  );

  const watchedValues = useWatch({
    control,
    name: ["fields", "name"],
    defaultValue: {
      fields: attachment?.fields || {},
      name: attachment?.name || "",
    },
  });
  const [fields, name] = watchedValues;

  const requiredInputs = useMemo(() => {
    return parentModuleDefinition?.inputs?.filter(
      (input) => input?.settings?.required === true,
    );
  }, [parentModuleDefinition?.inputs]);

  const isDisabled = useMemo(() => {
    if (!name || isEmpty(name)) {
      return true;
    }

    return requiredInputs?.some(
      (input) => !String(fields?.[input.name])?.length,
    );
  }, [fields, name, requiredInputs]);

  const renderedInputs = useMemo(() => {
    return parentModuleDefinition?.inputs?.map((input) => (
      <Controller
        key={input._id}
        name={`fields.${input.name}`}
        control={control}
        rules={{
          required: !!input?.settings?.required,
          maxLength: {
            value: input?.settings?.maxChar || Lengths.DESCRIPTION,
            message: t("error.length.less", {
              length: input?.settings?.maxChar || Lengths.DESCRIPTION,
            }),
          },
        }}
        render={({ field, fieldState }) =>
          getRenderedFieldByType(
            input,
            field,
            fieldState,
            input.label,
            undefined,
            {
              possibleBookedSlots: parentModuleDefinition?._possibleBookedSlots,
            },
          )
        }
      />
    ));
  }, [
    parentModuleDefinition?.inputs,
    parentModuleDefinition?._possibleBookedSlots,
    control,
    t,
  ]);

  const localOnClose = () => {
    onClose();
    reset({
      name: "",
      fields: {},
      moduleDefinitionId: "",
      moduleInstanceId: "",
      moduleInstanceAttachmentId: "",
      entryId: "",
      userId,
    });
  };

  const onSave = async () => {
    try {
      setLoading(true);

      if (isEmpty(attachment)) {
        return;
      }

      const dto = getValues();

      await dispatch(
        moduleActions.updateModuleInstanceAttachment(dto),
      ).unwrap();

      if (location.pathname.includes("/entries")) {
        const updatedEntry = await dispatch(
          entryActions.getEntryById({ entryId: attachment?.entryId!, userId }),
        ).unwrap();

        setEntry?.(updatedEntry);
      } else {
        const { data: newInstances } = await dispatch(
          moduleActions.getModuleInstances({
            userId,
            moduleDefinitionId: attachment?.parentModuleDefinitionId!,
            keyword: "",
            meta: PageMeta.create({
              page: instancesPage - 1,
              count: instancesPageSize,
            }),
          }),
        ).unwrap();

        const updatedInstance = newInstances?.find(
          (instance) => instance._id === attachment?.parentId,
        );

        if (isFunction(setSelectedInstance) && updatedInstance) {
          setSelectedInstance(updatedInstance);
        }
      }

      localOnClose();

      AtiraToast.success(t("modules.instance-attachment.update.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && attachment) {
      reset({
        userId,
        fields: attachment?.fields || {},
        moduleInstanceId: attachment?.parentId,
        moduleDefinitionId: attachment?.parentModuleDefinitionId,
        moduleInstanceAttachmentId: attachment._id,
        entryId: attachment?.entryId,
        name: attachment?.name,
      });
    }
  }, [open, attachment, userId, reset]);

  if (isEmpty(attachment) || !parentModuleDefinition) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onClose={localOnClose}
      title={t("modules.instance-attachment.update.title", {
        name: attachment?.name,
      })}
      maskClosable={false}
      extra={
        <DrawerDefaultsExtra
          onSubmit={handleSubmit(onSave)}
          onCancel={localOnClose}
          config={{
            loading,
            editMode: true,
            disabled: isDisabled,
          }}
        />
      }
      size="large"
      key={`${parentModuleDefinition._id}-${attachment._id}`}
    >
      <Flex width={"100%"} flexDirection="column" gap="m">
        <Controller
          control={control}
          name="name"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
            maxLength: {
              value: Lengths.DESCRIPTION,
              message: t("error.length.less", {
                name: t("modules.instance.name"),
                length: Lengths.DESCRIPTION,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t("modules.instance.name.title")}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              maxLength={Lengths.DESCRIPTION}
              info={t("modules.instance.name.info")}
              required
            />
          )}
        />

        {renderedInputs}
      </Flex>
    </Drawer>
  );
};
